import React, { useState } from 'react'
import NumberInputWithNull from '@components/common/form/NumberInputWithNull'
import VendorBlacklist from '@components/groupOrders/VendorBlacklist'
import MenuItemBlacklist from '@components/groupOrders/MenuItemBlacklist'
import Checkbox from '@components/common/form/Checkbox'
import {
  AddItemToBlacklistInput,
  AddVendorToBlacklistInput,
  VendorBlacklistEntry,
  ItemBlacklistEntry,
  UniversalMenuConfig,
} from '@types'
import { Label } from '@res/styledComponents/index'
import { ClosedBlacklistedBtn } from '@components/groupOrders/VendorBlacklist'
import Modal from '@components/common/modal/Modal'

interface UniversalMenuConfigModalProps {
  clientSettingsId: string
  currentConfig?: UniversalMenuConfig
  isClientFlatPriceEnabled: boolean
  loadChefs: any
  loadMenuItems: any
  // Item Blacklist
  addItemToBlacklist: (
    csId: string,
    input: AddItemToBlacklistInput,
  ) => Promise<ItemBlacklistEntry>
  updateItemBlacklistEntry: (
    csId: string,
    input: { itemId: string; active: boolean; reason: string },
  ) => Promise<ItemBlacklistEntry>

  // Vendor Blacklist
  addVendorToBlacklist: (
    csId: string,
    input: AddVendorToBlacklistInput,
  ) => Promise<VendorBlacklistEntry>
  updateVendorBlacklistEntry: (
    csId: string,
    input: { vendorId: string; active: boolean; reason: string },
  ) => Promise<VendorBlacklistEntry>

  dismissInfoModal: () => void
  showCustomModal: (val: any) => void
  hideModal: () => void
  saveConfig: (condfig: UniversalMenuConfig) => void
}

const UniversalMenuConfigModal: React.FC<UniversalMenuConfigModalProps> = ({
  clientSettingsId,
  currentConfig,
  loadChefs,
  loadMenuItems,
  isClientFlatPriceEnabled,

  addItemToBlacklist,
  updateItemBlacklistEntry,
  addVendorToBlacklist,
  updateVendorBlacklistEntry,

  dismissInfoModal,
  hideModal,
  showCustomModal,
  saveConfig,
}) => {
  const [enableSides, setEnableSides] = useState(
    currentConfig?.enableSides || false,
  )
  const [enableModifications, setEnabledModifications] = useState(
    currentConfig?.enableModifications || false,
  )
  const [maxItemPriceLimit, setMaxItemPriceLimit] = useState<number | null>(
    currentConfig?.maxItemPriceLimit || null,
  )
  const [flatPriceMarginMin, setFlatPriceMarginMin] = useState<number | null>(
    currentConfig?.flatPriceMarginMinimum || null,
  )
  const [vendorsBlacklist, setVendorBlacklist] = useState<
    VendorBlacklistEntry[]
  >(currentConfig?.vendorBlacklist || [])
  const [menuItemBlacklist, setMenuItemBlacklist] = useState<
    ItemBlacklistEntry[]
  >(currentConfig?.menuItemBlacklist || [])

  const onVendorBlacklistChange = (nextList: VendorBlacklistEntry[]) => {
    setVendorBlacklist(nextList)
  }

  const onMenuItemBlacklistChange = (nextList: ItemBlacklistEntry[]) => {
    setMenuItemBlacklist(nextList)
  }

  const onOpenVendorsBlacklist = () => {
    showCustomModal({
      title: 'Vendor Blacklist',
      content: (
        <VendorBlacklist
          editable={true}
          list={vendorsBlacklist}
          addVendorToBlacklist={(req) =>
            addVendorToBlacklist(clientSettingsId, req)
          }
          updateVendorBlacklistEntry={(req) =>
            updateVendorBlacklistEntry(clientSettingsId, req)
          }
          onChange={(nextList) => onVendorBlacklistChange(nextList)}
          close={dismissInfoModal}
          loadChefs={loadChefs}
        />
      ),
    })
  }

  const onOpenMenuItemsBlacklist = () => {
    showCustomModal({
      title: 'Menu Items Blacklist',
      content: (
        <MenuItemBlacklist
          editable={true}
          list={menuItemBlacklist}
          addItemToBlacklist={(req) =>
            addItemToBlacklist(clientSettingsId, req)
          }
          updateItemBlacklistEntry={(req) =>
            updateItemBlacklistEntry(clientSettingsId, req)
          }
          onChange={(nextList) => onMenuItemBlacklistChange(nextList)}
          loadChefs={loadChefs}
          loadMenuItems={loadMenuItems}
          close={dismissInfoModal}
        />
      ),
    })
  }

  const onSaveConfig = () => {
    const config: UniversalMenuConfig = {
      enableSides,
      enableModifications,
      maxItemPriceLimit,
      flatPriceMarginMinimum: flatPriceMarginMin,
      vendorBlacklist: vendorsBlacklist,
      menuItemBlacklist: menuItemBlacklist,
    }
    saveConfig(config)
  }

  return (
    <Modal
      title="Universal Menu Configuration"
      maxWidth="800px"
      width="800px"
      hideModal={hideModal}
    >
      <div className="space-y-4">
        <p className="text-gray-600 my-6 p-4 shadow-md rounded-md border border-solid border-gray-200">
          Universal menu configurations dictate which menu items are available
          on the group order menu and what the client and chef prices are.
        </p>

        <div className="flex flex-col space-y-2 p-4 border border-solid border-gray-200 shadow-md rounded-md w-90p">
          <Label>Item Type Preferences</Label>
          <Checkbox
            label="Enable Sides"
            checked={enableSides}
            onChange={() => setEnableSides(!enableSides)}
          />
          <Checkbox
            label="Enable Modifications"
            checked={enableModifications}
            onChange={() => setEnabledModifications(!enableModifications)}
          />
        </div>

        <div className="flex flex-col space-y-2 p-4 w-90p  border border-solid border-gray-200 shadow-md rounded-md">
          <div className="flex flex-col py-4 px-2 w-full">
            <Label>Max Item Price Limit</Label>
            <NumberInputWithNull
              value={maxItemPriceLimit}
              onChange={(val) => setMaxItemPriceLimit(val)}
              className="w-1/2 h-10"
            />
          </div>
          {isClientFlatPriceEnabled && (
            <div className="flex flex-col py-4 px-2  border-t-2 border-solid border-gray-4200">
              <Label>Flat Price Margin Minimum</Label>
              <NumberInputWithNull
                value={flatPriceMarginMin}
                onChange={(val) => setFlatPriceMarginMin(val)}
                min={0}
                max={100}
                step={0.1}
                asPercent={true}
                precision={2}
                className="w-1/2 rounded-md h-10"
              />
            </div>
          )}
        </div>

        <div className="flex flex-row items-center space-x-2 p-4 shadow-md rounded-md border border-solid border-gray-200">
          <div className="flex flex-col">
            <Label>Vendor Blacklist</Label>
            <ClosedBlacklistedBtn
              label="Blacklisted Vendors"
              listCount={vendorsBlacklist.filter((e) => e.active).length}
              toggleOpen={onOpenVendorsBlacklist}
            />
          </div>
          <div className="flex flex-col">
            <Label>Menu Item Blacklist</Label>
            <ClosedBlacklistedBtn
              label="Blacklisted Menu Items"
              listCount={menuItemBlacklist.filter((e) => e.active).length}
              toggleOpen={onOpenMenuItemsBlacklist}
            />
          </div>
        </div>

        <div className="flex flex-row items-center">
          <button
            className="py-2 px-3 flex items-center bg-green-600 text-white font-semibold rounded-lg hover:bg-green-400"
            onClick={onSaveConfig}
          >
            Save Universal Menu Configuration
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default UniversalMenuConfigModal
